import { Box, Button as MuiButton, CircularProgress, Tooltip } from "@mui/material";
import type { MouseEventHandler, Ref } from "react";
import React from "react";
import { Link } from "react-router-dom";
import InformationTooltip from "../informationTooltip/InformationTooltip";
import { useButtonStyles } from "./Button.styles";

export interface IButtonProps {
    id?: string;
    buttonType: "primary" | "secondary" | "success" | "danger" | "linkButton" | "green";
    onClick?: MouseEventHandler<HTMLElement>;
    startIcon?: React.ReactNode;
    endIcon?: React.ReactNode;
    children?: React.ReactNode;
    size?: "small" | "medium" | "large";
    disabled?: boolean;
    ariaControls?: string;
    ariaHaspopup?: boolean | "grid" | "listbox" | "menu" | "false" | "true" | "dialog" | "tree" | undefined;
    isOnlyIcon?: boolean;
    isLoading?: boolean;
    href?: string;
    onMouseHover?: (onEnter: boolean) => void;
    btnLength?: number;
    toolTip?: string;
    fullWidth?: boolean;
    showTooltipAsInfoIcon?: boolean;
    inputRef?: Ref<HTMLButtonElement>;
}

function Button(props: IButtonProps) {
    const styles = useButtonStyles(props);

    const onMouseOver = (onEnter: boolean) => {
        if (props.onMouseHover) props.onMouseHover(onEnter);
    };

    let buttonProps = {
        id: props.id,
        onMouseEnter: () => onMouseOver(true),
        onMouseLeave: () => onMouseOver(false),
        disabled: props.disabled || props.isLoading,
        size: props.size,
        startIcon: props.startIcon,
        endIcon: props.endIcon,
        "aria-controls": props.ariaControls,
        "aria-haspopup": props.ariaHaspopup,
    };

    const content = (
        <React.Fragment>
            {props.children}
            {props.isLoading && <CircularProgress size={24} sx={styles.buttonProgress} />}
        </React.Fragment>
    );

    let variant: "text" | "outlined" | "contained" = "contained";
    if (props.buttonType === "secondary") {
        variant = "outlined";
    }
    if (props.buttonType === "linkButton") {
        variant = "text";
    }

    const wrapperStyles =
        props.toolTip && props.showTooltipAsInfoIcon
            ? {
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
              }
            : {};

    const btn = (
        <Box sx={wrapperStyles}>
            {props.href ? (
                <MuiButton
                    variant={variant}
                    component={Link}
                    to={props.href}
                    onClick={(e) => (props.onClick ? props.onClick(e) : {})}
                    sx={styles.button}
                    {...buttonProps}
                >
                    {content}
                </MuiButton>
            ) : (
                <MuiButton ref={props.inputRef} variant={variant} onClick={props.onClick} sx={styles.button} {...buttonProps}>
                    {content}
                </MuiButton>
            )}
            {props.toolTip && props.showTooltipAsInfoIcon && <InformationTooltip text={props.toolTip} />}
        </Box>
    );

    if (props.toolTip && !props.showTooltipAsInfoIcon) {
        return (
            <Tooltip title={props.toolTip}>
                <span>{btn}</span>
            </Tooltip>
        );
    }

    return btn;
}

export default Button;
